/**
 * WCC360 v2 — status surfaces for the canonical report.
 *
 * These say true things. "Saved on this device" appears only when answers
 * really are still local; "Your full report is ready" appears only when a
 * sealed payload exists; a failed stage is named, not hidden behind
 * "something went wrong".
 */
import { AlertTriangle, Check, CheckCircle2, CloudOff, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import type { CanonicalTakeover } from "../useCanonicalReport";

const STAGE_LABEL: Record<string, string> = {
  assessment: "reading your answers",
  evidence: "storing your answers",
  features: "working out your patterns",
  human_intelligence: "building your profile",
  career_intelligence: "matching you to careers",
  recommendations: "ranking your matches",
  experience: "writing your report",
  reports: "sealing your report",
};

export function PendingWritesBanner({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div
      role="status"
      className="mt-8 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm
        text-amber-300 flex items-start gap-3"
    >
      <CloudOff className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
      <p>
        <span className="font-medium">
          Your career profile is safe on this device.
        </span>{" "}
        It syncs the moment you're back online — nothing about you is lost.
      </p>

    </div>
  );
}

/**
 * The other half of the honest answer: when nothing is pending, say so once,
 * and point at where completed attempts live.
 */
export function SavedConfirmation({ count, completedAt }: { count: number; completedAt?: string | null }) {
  if (count > 0) return null;
  const when = completedAt ? new Date(completedAt) : null;
  return (
    <div
      role="status"
      data-testid="v2-saved-confirmation"
      className="mt-8 rounded-2xl border border-primary/40 bg-primary/5 p-4 text-sm
        text-foreground/90 flex items-start gap-3"
    >
      <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
      <p>
        <span className="font-medium">Your career profile is saved.</span>{" "}
        Everything you shared{when ? ` at ${when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""} is shaping your matches. Return to your journey any time in{" "}
        <Link to="/hub/you#history" className="text-primary underline underline-offset-2">
          History &amp; milestones
        </Link>.
      </p>

    </div>
  );
}

export function CanonicalReadyBanner({ takeover }: { takeover: CanonicalTakeover }) {
  if (!takeover.ready) return null;
  return (
    <div
      role="status"
      className="mt-8 rounded-2xl border border-primary/50 bg-primary/10 p-4 text-sm
        flex items-start gap-3"
    >
      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
      <p>
        <span className="font-medium">Your career picture is complete.</span>{" "}
        Below is the full read on where you fit and why.{" "}
        <Link to="/report" className="text-primary underline underline-offset-2">
          See your career report
        </Link>
        .
      </p>

    </div>
  );
}

export function PipelineFailureBanner({ takeover }: { takeover: CanonicalTakeover }) {
  if (takeover.ready) return null;
  if (!takeover.failedStage) return null;
  const label = STAGE_LABEL[takeover.failedStage] ?? takeover.failedStage;
  return (
    <div
      role="status"
      className="mt-8 rounded-2xl border border-destructive/50 bg-destructive/10 p-4 text-sm
        space-y-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden />
        <p>
          <span className="font-medium">Your deeper career report is still {label}.</span>{" "}
          Your answers are safe, and your matches below already hold. One tap picks it
          back up.
        </p>

      </div>
      <button
        onClick={takeover.retry}
        disabled={takeover.retrying}
        className="min-h-[44px] px-4 rounded-xl border border-border bg-card hover:bg-secondary
          text-sm inline-flex items-center gap-2 disabled:opacity-60
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <RotateCcw className={`h-4 w-4 ${takeover.retrying ? "animate-spin" : ""}`} aria-hidden />
        {takeover.retrying ? "Picking it back up…" : "Try that again"}
      </button>
    </div>
  );
}
